<template>
  <!--
    MetricStudio — WHAT: the SHELL for the whole Metric Studio workbench. Renders
    the horizontal top tab bar (one tab per artifact type, in registry order) and
    switches the body between three modes: a full-page manager (Wizard /
    Engagement), the generic ArtifactList (when no record is selected), or the
    generic ArtifactDetail (when one is). It also loads the shared reference data
    once and owns create + delete for the list→detail artifacts.

    WHERE: the "Performance Domains & Metrics" tenant-admin tab, rendered by
    Overview_List_TenantAdmin.vue at /tenantadmin/metric-studio/:artifact.
    Top-level component of this folder; parent of ArtifactList / ArtifactDetail /
    QualityManager / EngagementManager / WizardManager.

    CONTRACT
      props: none (route-driven).
      emits: none.
      Consumes :artifact / :id route params for deep-linking. Passes
      sharedContext down to ArtifactDetail:
      { attributes, domains, domainsByMetric, metrics, opportunityPrimitives,
        opportunityPatientFlags }.
      Handles child events: ArtifactList @open/@create/@delete, ArtifactDetail
      @back/@saved, ConfirmationModal @confirm/@cancel.

    NOTES: reference data is fetched once (refDataLoaded guard) via parallel
    Promise.all; the opportunity-primitives and patient-flags calls are
    .catch()-defaulted so a 404 doesn't break the load. createRecord builds a
    per-artifact default payload (studio_config shapes differ per type); creating
    an insight requires an existing metric (toast otherwise). Tab identity uses the
    artifact key as the Page_Tabs id. Delete uses deleteUrl if present else
    detailUrl, and maps 400/409 to an "in use by performance domains" toast.
    Every artifact `icon` must be registered in src/main.js.
  -->
  <div class="ms2 flex flex-col h-[calc(100vh-56px)] bg-gray-50">
    <PageHeader
      title="Metric Studio"
      subtitle="Author metrics, flags, worklists, insights, and setup — one workbench"
      :breadcrumbs="[{ label: 'Admin Console' }]"
    >
      <!-- ─────────────── HORIZONTAL top tab bar (the seven artifacts) ─────────────── -->
      <template #bottom>
        <Page_Tabs
          class="px-6 pt-3"
          :tabs="tabItems"
          :initial-tab="activeKey"
          no-border
          @select="selectArtifact"
        />
      </template>
    </PageHeader>

    <!-- ─────────────── BODY: list | detail | full manager ─────────────── -->
    <main class="flex-1 min-h-0 flex flex-col">
      <!-- Full-manager artifacts (Wizard / Engagement) -->
      <component
        :is="artifact.manager"
        v-if="artifact.fullManager"
        :key="artifact.key"
      />

      <!-- List-detail artifacts -->
      <template v-else>
        <ArtifactDetail
          v-if="selectedId"
          :key="artifact.key + ':' + selectedId"
          :artifact="artifact"
          :record-id="selectedId"
          :context="sharedContext"
          @back="backToList"
          @saved="onRecordSaved"
        />
        <div v-else class="flex-1 min-h-0 flex flex-col">
          <!-- optional sub-tabs (e.g. Quality: Measures | Contract Assignments) -->
          <div v-if="managerTabs.length" class="px-4 pt-3 flex gap-1">
            <button
              v-for="t in subTabItems"
              :key="t.key"
              class="text-xs font-semibold px-3 py-1.5 rounded-lg"
              :class="activeSubTab === t.key
                ? 'bg-white border border-gray-200 text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-800'"
              @click="selectSubTab(t.key)"
            >{{ t.label }}</button>
          </div>

          <component
            :is="activeManagerTab.component"
            v-if="activeManagerTab"
            :key="artifact.key + ':' + activeManagerTab.key"
            class="flex-1 min-h-0"
          />
          <div v-else class="flex-1 min-h-0 p-4">
            <ArtifactList
              :artifact="artifact"
              :rows="rows"
              :loading="loading"
              :error="error"
              :creating="creating"
              @open="openRecord"
              @create="openCreate"
              @delete="confirmDelete"
            />
          </div>
        </div>
      </template>
    </main>

    <!-- create form -->
    <CreateArtifactModal
      v-if="artifact.create"
      :artifact="artifact"
      :is-visible="showCreate"
      :context="sharedContext"
      :submitting="creating"
      @submit="createRecord"
      @cancel="showCreate = false"
    />

    <!-- delete confirm: artifacts with a preview endpoint get the itemised
         dialog (blocked reasons / what else is affected); the rest keep the
         shared yes-no modal. -->
    <DeleteMetricModal
      v-if="artifact.deletePreviewUrl"
      :is-visible="!!pendingDelete"
      :noun="artifact.singular.toLowerCase()"
      :name="pendingDelete ? (pendingDelete[artifact.nameField || 'name'] || '') : ''"
      :preview="deletePreview"
      :loading="previewLoading"
      :load-error="previewError"
      :deleting="deleting"
      @confirm="doDelete"
      @cancel="closeDelete"
    />
    <ConfirmationModal
      v-else
      :is-visible="!!pendingDelete"
      :message="pendingDelete ? `Delete this ${artifact.singular.toLowerCase()}? This cannot be undone.` : ''"
      @confirm="doDelete"
      @cancel="closeDelete"
    />
    <div v-if="toast" class="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 shadow-lg z-50">
      {{ toast }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import ArtifactList from './core/ArtifactList.vue';
import ArtifactDetail from './core/ArtifactDetail.vue';
import CreateArtifactModal from './core/CreateArtifactModal.vue';
import DeleteMetricModal from './panels/DeleteMetricModal.vue';
import QualityManager from './artifacts/QualityManager.vue';
import QualityScoringManager from './artifacts/QualityScoringManager.vue';
import EngagementManager from './artifacts/EngagementManager.vue';
import WizardManager from './artifacts/WizardManager.vue';

import {
  ARTIFACTS, ARTIFACT_ORDER, getArtifact,
} from './core/artifactRegistry.js';
import {
  artifactKeyFor, artifactRouteSegment,
} from '../../../../router/tenantadmin/metric_studio_routes.js';
import { backTo } from '@/utils/backTo';

export default {
  name: 'MetricStudio',
  components: {
    PageHeader, Page_Tabs, ConfirmationModal, ArtifactList, ArtifactDetail,
    CreateArtifactModal, DeleteMetricModal, QualityManager, QualityScoringManager,
    EngagementManager, WizardManager,
  },
  data() {
    return {
      activeKey: artifactKeyFor(this.$route.params.artifact),
      selectedId: this.$route.name === 'AdminMetricStudioDetail' ? Number(this.$route.params.id) : null,
      subTab: null,          // managerTab key when its sub-tab is active
      rows: [],
      loading: false,
      error: null,
      creating: false,
      showCreate: false,
      pendingDelete: null,
      deletePreview: null,
      previewLoading: false,
      previewError: '',
      deleting: false,
      toast: null,

      // shared reference data (loaded once, handed to every detail/section)
      attributes: [],
      domains: [],
      domainsByMetric: {},
      metrics: [],           // lightweight, for pickers (worklist/insight parent)
      opportunityPrimitives: [],
      opportunityPatientFlags: [],
      dossierTemplates: [],       // patient Hermes templates, for opportunity dossier bindings
      refDataLoaded: false,
      loadedArtifactKey: null,
    };
  },
  computed: {
    artifact() { return getArtifact(this.activeKey) || ARTIFACTS.metric; },
    // Horizontal top tab bar: one tab per artifact type, in registry order.
    // Page_Tabs matches/emits by `id`, so we key tabs on the artifact key.
    tabItems() {
      // Guard against a missing/partial registry entry so a bad key can never
      // crash the whole header (it previously threw on a stale/corrupt bundle).
      return ARTIFACT_ORDER
        .map((k) => ARTIFACTS[k])
        .filter(Boolean)
        .map((a) => ({
          id: a.key,
          label: a.label,
          icon: ['fas', a.icon],
          description: a.help,
        }));
    },
    // An artifact may declare `managerTab` (one) or `managerTabs` (several);
    // both normalize to a list so the shell renders N sub-tabs beside the list.
    managerTabs() {
      const a = this.artifact;
      if (a.managerTabs) return a.managerTabs;
      return a.managerTab ? [a.managerTab] : [];
    },
    subTabItems() {
      if (!this.managerTabs.length) return [];
      return [{ key: 'list', label: this.artifact.label }, ...this.managerTabs];
    },
    activeSubTab() {
      return this.subTab || 'list';
    },
    activeManagerTab() {
      return this.managerTabs.find((t) => t.key === this.activeSubTab) || null;
    },
    sharedContext() {
      return {
        attributes: this.attributes,
        domains: this.domains,
        domainsByMetric: this.domainsByMetric,
        metrics: this.metrics,
        opportunityPrimitives: this.opportunityPrimitives,
        opportunityPatientFlags: this.opportunityPatientFlags,
        dossierTemplates: this.dossierTemplates,
      };
    },
  },
  watch: {
    $route() {
      this.syncRouteState();
    },
  },
  async mounted() {
    await this.loadRefData();
    await this.syncRouteState();
  },
  methods: {
    async loadRefData() {
      if (this.refDataLoaded) return;
      try {
        const [attrsRes, domainsRes, metricsRes, oppRes, flagRes, templatesRes] = await Promise.all([
          axios.get('api/impact_analytics/attributes/'),
          axios.get('api/performancedomains/'),
          axios.get('api/impact_analytics/composable-metrics/'),
          axios.get('api/impact_analytics/composable-metrics/opportunity-primitives/').catch(() => ({ data: { primitives: [] } })),
          axios.get('api/impact_analytics/composable-metrics/patient-flags/').catch(() => ({ data: { flags: [] } })),
          // patient Hermes templates for the opportunity dossier bindings editor.
          // .catch so a failure here degrades that one widget to "(current)"
          // options rather than blanking all of Metric Studio's reference data.
          axios.get('api/hermes/templates/').catch(() => ({ data: [] })),
        ]);
        const arr = (r) => (Array.isArray(r.data) ? r.data : r.data.results || []);
        this.attributes = arr(attrsRes);
        this.domains = arr(domainsRes);
        this.domainsByMetric = this.domains.reduce((acc, d) => {
          (d.composable_metrics || []).forEach((l) => {
            if (l.metric_id) (acc[l.metric_id] = acc[l.metric_id] || []).push({ id: d.id, name: d.name });
          });
          return acc;
        }, {});
        this.metrics = arr(metricsRes).sort((a, b) => a.id - b.id);
        this.opportunityPrimitives = (oppRes.data && oppRes.data.primitives) || [];
        this.opportunityPatientFlags = (flagRes.data && flagRes.data.flags) || [];
        this.dossierTemplates = arr(templatesRes)
          .filter((t) => t.is_active && t.unit === 'BENE_MBI_ID')
          .map((t) => ({ id: t.id, name: t.name }))
          .sort((a, b) => a.id - b.id);
        this.refDataLoaded = true;
      } catch (e) {
        this.error = `Failed to load reference data: ${e?.response?.data?.detail || e.message}`;
      }
    },

    async syncRouteState() {
      const key = artifactKeyFor(this.$route.params.artifact);
      const artifactChanged = this.activeKey !== key;
      this.activeKey = key;
      this.selectedId = this.$route.name === 'AdminMetricStudioDetail'
        ? Number(this.$route.params.id)
        : null;
      if (artifactChanged) {
        this.subTab = null;
        this.rows = [];
      }
      if (!this.artifact.fullManager && this.loadedArtifactKey !== key) {
        await this.loadRows(key);
      }
    },

    selectArtifact(key) {
      if (!ARTIFACTS[key]) return;
      if (this.$route.name === 'AdminMetricStudioList' && this.activeKey === key) return;
      this.$router.push({
        name: 'AdminMetricStudioList',
        params: { artifact: artifactRouteSegment(key) },
      });
    },

    async loadRows(key = this.activeKey) {
      const artifact = getArtifact(key);
      if (!artifact || artifact.fullManager) return;
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(artifact.listUrl);
        const all = Array.isArray(data) ? data : (data.results || []);
        // metric and quality share one endpoint; rowFilter splits them.
        if (this.activeKey !== key) return;
        this.rows = artifact.rowFilter ? all.filter(artifact.rowFilter) : all;
        this.loadedArtifactKey = key;
        if (key === 'metric') this.loadAuditBoard();
      } catch (e) {
        if (this.activeKey === key) {
          this.error = `Failed to load: ${e?.response?.data?.detail || e.message}`;
        }
      } finally {
        if (this.activeKey === key) this.loading = false;
      }
    },

    async loadAuditBoard() {
      // Contract-sync + per-stage verdict columns. Deliberately NOT awaited by
      // loadRows: the board includes a Snowflake read, and the list must render
      // without it. Best-effort — on failure the columns just show "—".
      try {
        const { data } = await axios.get('api/impact_analytics/composable-metrics/audit-board/');
        if (this.activeKey !== 'metric') return; // user switched tabs mid-flight
        this.rows = this.rows.map((r) => ({ ...r, _audit: data[r.id] || null }));
      } catch (e) {
        console.error('audit board load failed:', e?.response?.data?.error || e.message);
      }
    },

    selectSubTab(key) {
      this.subTab = key;
    },

    openRecord(row) {
      this.$router.push({
        name: 'AdminMetricStudioDetail',
        params: {
          artifact: artifactRouteSegment(this.activeKey),
          id: row[this.artifact.idField],
        },
      });
    },
    backToList() {
      const location = {
        name: 'AdminMetricStudioList',
        params: { artifact: artifactRouteSegment(this.activeKey) },
      };
      backTo(this.$router, location);
    },
    async onRecordSaved() {
      // keep the metric picker/domains fresh after a save
      if (this.activeKey === 'metric') await this.loadRefData();
    },

    // ── create ──
    // ── create ──
    openCreate() {
      if (this.artifact.key === 'insight' && !this.metrics.length) {
        this.showToast('Create a metric first — an insight belongs to a metric.');
        return;
      }
      if (!this.artifact.create) return;
      this.showCreate = true;
    },

    /** Structural defaults; the modal's values are merged over these. */
    createDefaults(key) {
      if (key === 'metric' || key === 'quality') {
        return {
          calculation_type: 'composable',
          composable_config: {
            use_studio_engine: true, aggregation_type: 'percentage',
            primary_key_columns: ['BENE_MBI_ID'],
            studio_config: { steps: [], aggregation_type: 'percentage', primary_key_columns: ['BENE_MBI_ID'] },
          },
          display_config: {},
        };
      }
      if (key === 'flag') {
        return {
          color: 'gray', priority: 99, is_active: true,
          studio_config: { steps: [], primary_key_columns: ['BENE_MBI_ID'] },
        };
      }
      if (key === 'worklist') {
        return {
          is_active: true,
          studio_config: { steps: [], primary_key_columns: ['BENE_MBI_ID'] },
          column_config: [],
        };
      }
      if (key === 'insight') {
        return {
          insight_id: Date.now() % 100000, insight_type: 'metric_insight', is_active: true,
          studio_config: { steps: [] }, viz_type: 'chart_v5', viz_config: {},
          layout: { width: 'full' }, display_order: 0,
        };
      }
      return {};
    },

    async createRecord(values) {
      const key = this.activeKey;
      this.creating = true;
      try {
        const payload = { ...this.createDefaults(key), ...values };
        const { data } = await axios.post(this.artifact.listUrl, payload);
        this.showCreate = false;
        await this.loadRows();
        this.$router.push({
        name: 'AdminMetricStudioDetail',
        params: {
          artifact: artifactRouteSegment(key),
          id: data[this.artifact.idField],
          },
        });
      } catch (e) {
        // Surface DRF field errors ({field: [msg]}), not just `detail`.
        const d = e?.response?.data;
        let msg = d?.detail;
        if (!msg && d && typeof d === 'object') {
          msg = Object.entries(d)
            .map(([f, v]) => `${f}: ${Array.isArray(v) ? v.join(' ') : v}`)
            .join(' · ');
        }
        this.showToast(`Failed to create: ${msg || e.message}`);
      } finally {
        this.creating = false;
      }
    },

    // ── delete ──
    async confirmDelete(row) {
      this.pendingDelete = row;
      this.deletePreview = null;
      this.previewError = '';
      if (!this.artifact.deletePreviewUrl) return;
      this.previewLoading = true;
      try {
        const { data } = await axios.get(
          this.artifact.deletePreviewUrl(row[this.artifact.idField])
        );
        // Guard against a stale response landing after the user moved on.
        if (this.pendingDelete === row) this.deletePreview = data;
      } catch (e) {
        if (this.pendingDelete === row) {
          this.previewError = `Couldn’t check what depends on this: ${e?.response?.data?.detail || e.message}`;
        }
      } finally {
        this.previewLoading = false;
      }
    },
    closeDelete() {
      this.pendingDelete = null;
      this.deletePreview = null;
      this.previewError = '';
    },
    async doDelete() {
      const row = this.pendingDelete;
      if (!row || this.deleting) return;
      const url = this.artifact.deleteUrl
        ? this.artifact.deleteUrl(row[this.artifact.idField])
        : this.artifact.detailUrl(row[this.artifact.idField]);
      this.deleting = true;
      try {
        await axios.delete(url);
        this.rows = this.rows.filter((r) => r[this.artifact.idField] !== row[this.artifact.idField]);
        this.closeDelete();
      } catch (e) {
        // The server re-checks, so it can refuse something the preview cleared
        // (a domain added meanwhile). Show its reasons rather than a guess.
        const body = e?.response?.data;
        if ([400, 409].includes(e?.response?.status) && body?.blockers?.length) {
          this.deletePreview = { ...(this.deletePreview || {}), ...body };
        } else {
          this.closeDelete();
          this.showToast(`Delete failed: ${body?.error || body?.detail || e.message}`);
        }
      } finally {
        this.deleting = false;
      }
    },

    showToast(msg) { this.toast = msg; setTimeout(() => { this.toast = null; }, 5000); },
  },
};
</script>

<style scoped>
.ms2-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.ms2-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 10px; border: 3px solid transparent; background-clip: content-box; }
.ms2-scroll::-webkit-scrollbar-track { background: transparent; }
</style>
